import * as THREE from 'three';
import { Water } from 'three/examples/jsm/objects/Water.js';
import { computeRegionBounds } from '../utils/geometry';
import type { WaterFeature } from '../course/WaterFeature';
import type { MaterialLibrary } from './MaterialLibrary';
import type { Terrain } from '../terrain/Terrain';

/**
 * Builds real animated water using three's official Water object: a
 * procedurally generated (tileable) normal map animates over time for
 * ripple motion, and a live mirror render target gives real reflection —
 * not a static flat plane. Refraction is architected for, not implemented:
 * Water.js is reflection-only; a refraction pass needs a second render
 * target sampling what's *beneath* the water plane (typically via a
 * depth/color pre-pass with a clip plane), which is a meaningfully bigger
 * addition than swapping a uniform — worth doing once there's an actual
 * ball that needs to look right entering water, not before.
 *
 * Shoreline blending: since each course's water regions are circles/
 * polygons of arbitrary shape but Water.js's geometry is a plain
 * rectangular plane sized to the region's bounding box, a radial alpha
 * fade (patched into Water.js's own shader via onBeforeCompile, reusing
 * its existing `worldPosition` varying) softens the mesh's rectangular
 * edge into the shoreline rather than showing a hard box outline. This is
 * a real, working blend, not just a documented intent — future ripples/
 * ball splashes plug in as additional uniforms on this same material
 * without needing to touch geometry or add renderer plumbing.
 */
export class WaterRenderer {
  private readonly waterMeshes: Water[] = [];
  private readonly fillMeshes: THREE.Mesh[] = [];
  private elapsedSeconds = 0;

  render(
    waterFeatures: readonly WaterFeature[],
    materials: MaterialLibrary,
    sunDirection: THREE.Vector3,
    parent: THREE.Object3D,
    terrain?: Terrain,
  ): void {
    for (const water of waterFeatures) {
      const built = this.buildWater(water, materials, sunDirection, terrain);
      parent.add(built.fill);
      parent.add(built.water);
      this.fillMeshes.push(built.fill);
      this.waterMeshes.push(built.water);
    }
  }

  /** Advances every water surface's animated ripple time — call from Engine's update loop. */
  update(deltaSeconds: number): void {
    this.elapsedSeconds += deltaSeconds;
    for (const mesh of this.waterMeshes) {
      (mesh.material as THREE.ShaderMaterial).uniforms.time.value = this.elapsedSeconds;
    }
  }

  private buildWater(
    water: WaterFeature,
    materials: MaterialLibrary,
    sunDirection: THREE.Vector3,
    terrain?: Terrain,
  ): { water: Water; fill: THREE.Mesh } {
    const bounds = computeRegionBounds(water.def.region);
    const width = Math.max(1, bounds.width);
    const depth = Math.max(1, bounds.depth);
    const displayLevel = terrain
      ? Math.max(water.def.waterLevel, terrain.getHeightAt(bounds.centerX, bounds.centerZ) + 0.045)
      : water.def.waterLevel;
    const geometry = waterGeometry(water.def.region);
    const fill = new THREE.Mesh(
      waterGeometry(water.def.region),
      new THREE.MeshBasicMaterial({
        color: 0x003f8f,
        transparent: true,
        opacity: 0.88,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    );
    fill.rotation.x = water.def.region.kind === 'polygon' ? Math.PI / 2 : -Math.PI / 2;
    fill.position.set(
      water.def.region.kind === 'polygon' ? 0 : bounds.centerX,
      displayLevel + 0.008,
      water.def.region.kind === 'polygon' ? 0 : bounds.centerZ,
    );
    fill.name = `WaterFill:${water.def.id}`;
    fill.renderOrder = 1;

    const mesh = new Water(geometry, {
      textureWidth: 512,
      textureHeight: 512,
      waterNormals: materials.waterNormalMap(),
      sunDirection: sunDirection.clone(),
      sunColor: 0xffffff,
      waterColor: 0x003f8f,
      distortionScale: 2.2,
      alpha: 0.96,
      fog: false,
    });
    mesh.rotation.x = water.def.region.kind === 'polygon' ? Math.PI / 2 : -Math.PI / 2;
    mesh.position.set(
      water.def.region.kind === 'polygon' ? 0 : bounds.centerX,
      displayLevel,
      water.def.region.kind === 'polygon' ? 0 : bounds.centerZ,
    );
    mesh.name = `WaterFeature:${water.def.id}`;
    mesh.receiveShadow = true;
    mesh.renderOrder = 2;
    (mesh.material as THREE.Material).depthWrite = false;

    patchShorelineFade(mesh, bounds.centerX, bounds.centerZ, Math.min(width, depth) / 2);

    return { water: mesh, fill };
  }

  dispose(): void {
    for (const mesh of this.waterMeshes) {
      mesh.parent?.remove(mesh);
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    }
    for (const mesh of this.fillMeshes) {
      mesh.parent?.remove(mesh);
      mesh.geometry.dispose();
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      materials.forEach((material) => material.dispose());
    }
    this.waterMeshes.length = 0;
    this.fillMeshes.length = 0;
  }
}

function waterGeometry(region: WaterFeature['def']['region']): THREE.BufferGeometry {
  return region.kind === 'polygon'
    ? new THREE.ShapeGeometry(
        new THREE.Shape(region.points.map((p) => new THREE.Vector2(p.x, p.z))),
      )
    : new THREE.CircleGeometry(region.radius, 64);
}

/**
 * Patches Water.js's shader (a raw ShaderMaterial, not MeshStandardMaterial
 * — no chunk system to hook into) to fade alpha to 0 near the edge of the
 * mesh's bounding radius, reusing the `worldPosition` varying it already
 * declares/populates. Approximates the region's actual shape with a
 * circle, which is exact for our demo pond and a reasonable soft-edge
 * approximation for irregular shapes until real shoreline geometry exists.
 */
function patchShorelineFade(water: Water, centerX: number, centerZ: number, radius: number): void {
  const material = water.material as THREE.ShaderMaterial;
  material.uniforms.shorelineCenter = { value: new THREE.Vector2(centerX, centerZ) };
  material.uniforms.shorelineRadius = { value: radius };

  const previousOnBeforeCompile = material.onBeforeCompile.bind(material);
  material.onBeforeCompile = (shader) => {
    previousOnBeforeCompile(shader, undefined as unknown as THREE.WebGLRenderer);
    shader.uniforms.shorelineCenter = material.uniforms.shorelineCenter;
    shader.uniforms.shorelineRadius = material.uniforms.shorelineRadius;

    shader.fragmentShader = shader.fragmentShader
      .replace(
        'uniform float alpha;',
        `uniform float alpha;
        uniform vec2 shorelineCenter;
        uniform float shorelineRadius;`,
      )
      .replace(
        'gl_FragColor = vec4( outgoingLight, alpha );',
        `float shorelineDist = distance( worldPosition.xz, shorelineCenter );
        float shorelineFade = 1.0 - smoothstep( shorelineRadius * 0.82, shorelineRadius, shorelineDist );
        gl_FragColor = vec4( outgoingLight, alpha * shorelineFade );`,
      );
  };
}
